import { UPDATE_DASHBOARD_IN_PROGRESS_INFO } from '@modules/dashboard/constants';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { Box, BoxProps, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';

interface DashboardCellResizerProps {
  rowID: string;
  rowHeight: number;
  sx?: BoxProps['sx'];
  showDropIndicator?: boolean;
  skipInteractions?: boolean;
  onMouseDown?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

export function DashboardCellResizer({
  rowID,
  rowHeight,
  onMouseDown: onMouseDownProp,
  showDropIndicator = false,
  skipInteractions = false,
  sx,
}: DashboardCellResizerProps) {
  const [bottom, setBottom] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [rowElement, setRowElement] = useState<Element | null>(null);
  const isUpdateDashboardInProgress = useDashboardStore(
    ({ visual }) => visual.isUpdateDashboardInProgress,
  );

  useEffect(() => {
    setRowElement(document.querySelector(`[data-id='${rowID}']`));
  }, [rowID]);

  useEffect(() => {
    const onMouseUp = () => {
      setIsActive(false);
    };

    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  if (!rowElement) {
    return null;
  }

  const onMouseDown = skipInteractions
    ? undefined
    : (eve: React.MouseEvent<HTMLDivElement>) => {
        setIsActive(true);
        onMouseDownProp?.(eve);
      };

  const onMouseMove = skipInteractions
    ? undefined
    : (eve: React.MouseEvent) => {
        const offsetY = 15;
        const cursorRelativePositionY =
          rowElement.getBoundingClientRect().bottom - eve.clientY;

        if (
          cursorRelativePositionY - offsetY >= 0 &&
          cursorRelativePositionY + offsetY <= rowHeight
        ) {
          setBottom(cursorRelativePositionY + offsetY);
        }
      };

  const lineElement = (
    <Box
      sx={{
        height: '100%',
        margin: 'auto',
        width: '4px',
        boxSizing: 'border-box',
        backgroundColor: 'none',
        borderRadius: '5px',
        opacity: showDropIndicator ? 0.75 : 0.2,
        transition: 'background-color .25s ease 0s',
      }}
    />
  );

  const handleElement =
    skipInteractions || showDropIndicator ? null : (
      <Tooltip
        title={
          isUpdateDashboardInProgress ? UPDATE_DASHBOARD_IN_PROGRESS_INFO : null
        }
      >
        <Box
          sx={{
            boxSizing: 'border-box',
            bottom,
            backgroundColor: 'none',
            borderRadius: '5px',
            height: '32px',
            margin: 'auto',
            position: 'relative',
            transition: 'background-color .25s ease 0s',
            width: '6px',
          }}
        ></Box>
      </Tooltip>
    );

  return (
    <Box
      className="cell-resizer"
      sx={{
        height: `${rowHeight}px`,
        display: 'flex',
        width: '20px',
        justifyContent: 'center',
        ...sx,
      }}
    >
      <Box
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        sx={{
          cursor:
            showDropIndicator || !skipInteractions ? 'ew-resize' : 'initial',
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
          '> *': {
            backgroundColor:
              isActive || showDropIndicator
                ? (theme) => theme.palette.primary.main
                : 'none',
          },
          '&:hover': {
            '> *': {
              backgroundColor:
                showDropIndicator || !skipInteractions
                  ? (theme) => theme.palette.primary.main
                  : 'none',
            },
          },
        }}
      >
        {lineElement}
        {handleElement}
      </Box>
    </Box>
  );
}
